import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PublicLayout from "../components/PublicLayout";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Bus } from "lucide-react";
import { useT } from "../lib/i18n";

function SeatMap({ capacity, taken, selected, setSelected, max, t }) {
  const seats = Array.from({length: capacity}, (_,i)=>i+1);
  const toggle = (n) => {
    if (taken.includes(n)) return;
    if (selected.includes(n)) setSelected(selected.filter(s=>s!==n));
    else if (selected.length < max) setSelected([...selected, n]);
    else toast.warning(`You can pick up to ${max} seats.`);
  };
  return (
    <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
      <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">{t("bf.front_driver")}</div>
      <div className="grid grid-cols-4 gap-2">
        {seats.map(n => {
          const isTaken = taken.includes(n);
          const isSel = selected.includes(n);
          return (
            <button type="button" key={n} onClick={()=>toggle(n)} disabled={isTaken}
              data-testid={`seat-${n}`}
              className={`seat-btn h-10 rounded-md text-sm font-semibold border ${isTaken?"bg-slate-300 text-slate-500 border-slate-300 cursor-not-allowed": isSel?"bg-[#ea580c] text-white border-[#ea580c]":"bg-white text-slate-700 border-slate-300 hover:border-[#ea580c]"}`}>{n}</button>
          );
        })}
      </div>
      <div className="flex gap-4 text-xs mt-4 text-slate-500">
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-white border border-slate-300" /> {t("bf.seat_avail")}</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#ea580c]" /> {t("bf.seat_selected")}</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-slate-300" /> {t("bf.seat_booked")}</span>
      </div>
    </div>
  );
}

export default function BookingForm() {
  const t = useT();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const routeId = sp.get("route_id");
  const bookingType = sp.get("booking_type") || "vehicle_hire";
  const passengers = parseInt(sp.get("passengers")) || 1;
  const travelDate = sp.get("travel_date") || new Date().toISOString().slice(0,10);
  const [route, setRoute] = useState(null);
  const [taken, setTaken] = useState([]);
  const [seats, setSeats] = useState([]);
  const [form, setForm] = useState({
    customer_name:"", customer_mobile:"", customer_whatsapp:"", customer_email:"",
    from_place: sp.get("from_place")||"", to_place: sp.get("to_place")||"",
    travel_date: travelDate, departure_time: "",
    passengers, ac: sp.get("ac")==="false" ? false : true,
    vehicle_type_pref: "Tempo Traveller",
    pickup_location:"", drop_location:"", notes:"",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (routeId) {
      api.post("/routes/search", { from_place: sp.get("from_place"), to_place: sp.get("to_place"), travel_date: travelDate, passengers }).then(r=>{
        const rt = r.data.find(x=>x.id===routeId);
        if (rt) {
          setRoute(rt);
          setForm(f => ({...f, from_place: rt.from_place, to_place: rt.to_place, departure_time: rt.departure_time, ac: rt.ac}));
          api.get(`/bookings/${rt.id}`).catch(()=>{});
        }
      });
      api.get(`/routes`).then(r=>{
        const rt = r.data.find(x=>x.id===routeId);
        if (rt) {
          setRoute(rt);
          setForm(f => ({...f, from_place: rt.from_place, to_place: rt.to_place, departure_time: rt.departure_time, ac: rt.ac}));
        }
      });
    }
  }, [routeId, sp, travelDate, passengers]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        booking_type: bookingType,
        customer_name: form.customer_name, customer_mobile: form.customer_mobile,
        customer_whatsapp: form.customer_whatsapp || form.customer_mobile,
        customer_email: form.customer_email,
        from_place: form.from_place, to_place: form.to_place,
        travel_date: form.travel_date, departure_time: form.departure_time,
        passengers: parseInt(form.passengers)||1, ac: !!form.ac,
        vehicle_type_pref: form.vehicle_type_pref,
        pickup_location: form.pickup_location, drop_location: form.drop_location,
        route_id: routeId || null, seats: routeId ? seats : [],
        notes: form.notes,
      };
      const r = await api.post("/bookings", payload);
      toast.success(`Booking confirmed! ID: ${r.data.booking_id}`);
      nav(`/book/confirm/${r.data.booking_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed");
    }
    setLoading(false);
  };

  const fare = useMemo(() => {
    if (route) return route.price_per_seat * (seats.length || form.passengers);
    const base = { "Ertiga":3000, "Innova":4000, "Tempo Traveller":6500, "Traveller":6500, "Mini Bus":9000, "Bus":12000 }[form.vehicle_type_pref] || 5000;
    return bookingType==="multi_day" || bookingType==="outstation" ? base*2 : base;
  }, [route, seats, form.passengers, form.vehicle_type_pref, bookingType]);

  const step2Hint = t("bf.step2_hint").replace("{n}", form.passengers).replace("{name}", route?.name || "");

  return (
    <PublicLayout>
      <section className="max-w-6xl mx-auto px-4 pt-10 pb-16 grid lg:grid-cols-3 gap-8">
        <form onSubmit={submit} className="lg:col-span-2 space-y-6" data-testid="booking-form">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#ea580c] font-semibold">{t("bf.eyebrow")}</div>
            <h1 className="text-3xl font-display font-black tracking-tight">{t("bf.title")}</h1>
          </div>

          {route && (
            <div className="p-6 rounded-2xl bg-white border border-slate-200">
              <div className="font-display font-bold text-lg">{t("bf.step1")}</div>
              <div className="mt-3 flex items-center gap-4 p-4 rounded-xl bg-slate-50 border border-slate-200" data-testid="route-vehicle-card">
                <div className="w-16 h-16 rounded-lg bg-[var(--brand-navy)] text-white grid place-items-center flex-shrink-0">
                  <Bus className="w-7 h-7 text-[var(--brand-orange)]" />
                </div>
                <div className="min-w-0">
                  <div className="font-display font-bold text-[var(--brand-navy)]">{route.vehicle_type}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{route.seat_capacity}-{t("bf.seater")} · {route.ac?t("bf.ac"):t("bf.nonac")} · {t("bf.assigned_by_ops")}</div>
                </div>
              </div>
              <div className="font-display font-bold text-lg mt-5">{t("bf.step2")}</div>
              <div className="text-sm text-slate-500 mb-4">{step2Hint}</div>
              <SeatMap capacity={route.seat_capacity} taken={taken} selected={seats} setSelected={setSeats} max={form.passengers} t={t} />
            </div>
          )}

          <div className="p-6 rounded-2xl bg-white border border-slate-200 grid md:grid-cols-2 gap-4">
            <div><Label>{t("bf.full_name")} *</Label><Input required value={form.customer_name} onChange={e=>setForm({...form,customer_name:e.target.value})} data-testid="cust-name" /></div>
            <div><Label>{t("bf.mobile")} *</Label><Input required value={form.customer_mobile} onChange={e=>setForm({...form,customer_mobile:e.target.value})} data-testid="cust-mobile" /></div>
            <div><Label>{t("bf.whatsapp")}</Label><Input value={form.customer_whatsapp} placeholder={t("bf.same_as_mobile")} onChange={e=>setForm({...form,customer_whatsapp:e.target.value})} data-testid="cust-whatsapp" /></div>
            <div><Label>{t("bf.email")}</Label><Input type="email" value={form.customer_email} onChange={e=>setForm({...form,customer_email:e.target.value})} data-testid="cust-email" /></div>
            <div><Label>{t("bf.from")}</Label><Input required value={form.from_place} onChange={e=>setForm({...form,from_place:e.target.value})} data-testid="cust-from" /></div>
            <div><Label>{t("bf.to")}</Label><Input required value={form.to_place} onChange={e=>setForm({...form,to_place:e.target.value})} data-testid="cust-to" /></div>
            <div><Label>{t("bf.travel_date")} *</Label><Input required type="date" value={form.travel_date} onChange={e=>setForm({...form,travel_date:e.target.value})} data-testid="cust-date" /></div>
            <div><Label>{t("bf.departure_time")}</Label><Input type="time" value={form.departure_time} onChange={e=>setForm({...form,departure_time:e.target.value})} data-testid="cust-time" /></div>
            <div><Label>{t("bf.passengers")} *</Label><Input required type="number" min="1" value={form.passengers} onChange={e=>setForm({...form,passengers:e.target.value})} data-testid="cust-pax" /></div>
            {!routeId && (
              <>
            <div>
              <Label>{t("bf.vehicle_pref")}</Label>
              <Select value={form.vehicle_type_pref} onValueChange={v=>setForm({...form,vehicle_type_pref:v})}>
                <SelectTrigger data-testid="cust-vehicle"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Ertiga">Ertiga (6-seat)</SelectItem>
                  <SelectItem value="Innova">Innova (7-seat)</SelectItem>
                  <SelectItem value="Tempo Traveller">Tempo Traveller (20-seat)</SelectItem>
                  <SelectItem value="Traveller">Traveller (17-seat)</SelectItem>
                  <SelectItem value="Mini Bus">Mini Bus (25-seat)</SelectItem>
                  <SelectItem value="Bus">Bus (30-seat)</SelectItem>
                </SelectContent>
              </Select>
            </div>
                <div>
                  <Label>{t("bf.ac_pref")}</Label>
                  <Select value={String(form.ac)} onValueChange={v=>setForm({...form,ac:v==="true"})}>
                    <SelectTrigger data-testid="cust-ac"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="true">{t("bf.ac")}</SelectItem><SelectItem value="false">{t("bf.nonac")}</SelectItem></SelectContent>
                  </Select>
                </div>
              </>
            )}
            <div className="md:col-span-2"><Label>{t("bf.pickup")}</Label><Input value={form.pickup_location} onChange={e=>setForm({...form,pickup_location:e.target.value})} placeholder={t("bf.pickup_ph")} data-testid="cust-pickup" /></div>
            <div className="md:col-span-2"><Label>{t("bf.drop")}</Label><Input value={form.drop_location} onChange={e=>setForm({...form,drop_location:e.target.value})} data-testid="cust-drop" /></div>
            <div className="md:col-span-2"><Label>{t("bf.notes")}</Label><Textarea value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} data-testid="cust-notes" /></div>
          </div>
        </form>

        <aside>
          <div className="sticky top-24 p-6 rounded-2xl bg-white border border-slate-200 shadow-sm">
            <div className="font-display font-bold text-lg">{t("bf.fare_summary")}</div>
            <div className="mt-4 text-sm space-y-1.5 text-slate-600">
              <div className="flex justify-between"><span>{t("bf.f_type")}</span><span className="font-medium text-slate-900">{bookingType.replace(/_/g,' ')}</span></div>
              {route && <div className="flex justify-between"><span>{t("bf.f_route")}</span><span className="font-medium text-slate-900">{route.name}</span></div>}
              {route && <div className="flex justify-between"><span>{t("bf.f_seats")}</span><span className="font-medium text-slate-900">{seats.length || form.passengers}</span></div>}
              {!route && <div className="flex justify-between"><span>{t("bf.f_vehicle")}</span><span className="font-medium text-slate-900">{form.vehicle_type_pref}</span></div>}
              <div className="flex justify-between"><span>{t("bf.f_pax")}</span><span className="font-medium text-slate-900">{form.passengers}</span></div>
            </div>
            <div className="border-t border-slate-200 mt-4 pt-4 flex items-end justify-between">
              <div className="text-xs text-slate-500">{t("bf.total")}</div>
              <div className="text-3xl font-black text-[#1e1b4b]">₹{fare.toLocaleString()}</div>
            </div>
            <Button disabled={loading} onClick={submit} className="w-full mt-4 bg-[#ea580c] hover:bg-orange-700" data-testid="confirm-booking-btn">{loading?t("bf.booking_dots"):t("bf.confirm")}</Button>
            <div className="text-xs text-slate-500 mt-3 leading-relaxed">{t("bf.wa_after")}</div>
          </div>
          <div className="mt-3 p-3 rounded-xl bg-amber-50 border-l-4 border-amber-500 text-xs text-amber-900" data-testid="form-early-arrival-notice">
            {t("bf.early_arrival")}
          </div>
        </aside>
      </section>
    </PublicLayout>
  );
}
